import { useState } from 'react';
import { Users, UserPlus, Trash2, Info, Clock } from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Field, Input, Select, Modal, CopyField,
  Table, THead, TBody, TR, TH, TD, StatusBadge, ErrorNotice, Loading, EmptyState, ConfirmDialog,
} from '../../components/ui/index.js';
import { PageHero } from '../../components/PageHero.jsx';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { useAuth } from '../../lib/auth.jsx';
import { http } from '../../lib/api.js';
import { can, ROLE } from '../../lib/roles.js';
import { formatTimestamp } from '../../lib/format.js';

/**
 * Inviting somebody, rather than setting a password for them.
 *
 * An owner who types a password for their cashier has created a credential two
 * people know, and the shop then has no way to prove which of them used it. The
 * invited person chooses their own and only they ever know it.
 */
function InviteDialog({ open, onClose, roles, myRole, onInvited }) {
  const [form, setForm] = useState({ email: '', name: '', role: ROLE.STAFF });
  const [result, setResult] = useState(null);

  const { mutate, loading, error, reset } = useMutation(async () => {
    setResult(await http.post('/dashboard/team', form));
    onInvited?.();
  });

  const close = () => {
    setResult(null);
    setForm({ email: '', name: '', role: ROLE.STAFF });
    reset();
    onClose();
  };

  /* ---- after inviting: the link, shown once ---- */
  if (result) {
    const link = `${window.location.origin}/accept-invite?token=${encodeURIComponent(result.inviteToken)}`;
    return (
      <Modal
        open={open}
        onClose={close}
        title={`Invite ${result.member.name}`}
        blurb="Send them this link. It works once, and only they will know the password they choose."
        footer={<Button onClick={close}>Done</Button>}
      >
        <div className="space-y-4">
          <CopyField label="Invitation link" value={link} />

          {/*
            * There is no mailer yet, and saying "we've emailed them" when
            * nothing was sent is the kind of small lie that costs a support
            * ticket and some trust. So the inviter hands it over themselves.
            */}
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
            <p className="text-xs leading-relaxed text-amber-800">
              We have not emailed this — send it yourself, over something you already trust.
              It expires in 7 days and cannot be shown again.
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Invite somebody"
      blurb="They set their own password. You never see it."
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={loading}>Cancel</Button>
          <Button loading={loading} onClick={() => mutate().catch(() => {})}>Create invitation</Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mutate().catch(() => {}); }}>
        {error && <ErrorNotice error={error} title="Could not invite them" />}

        <Field label="Email" required error={error?.fieldErrors?.email}>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="them@yourshop.com"
              autoFocus
            />
          )}
        </Field>

        <Field label="Name" hint="Optional. They can change it when they accept.">
          {(props) => (
            <Input
              {...props}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ravi Iyer"
            />
          )}
        </Field>

        <Field label="Role" required>
          {(props) => (
            <Select
              {...props}
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            >
              {roles
                // Only an owner may create another owner.
                .filter((r) => r.key !== ROLE.OWNER || can(myRole, 'MANAGE_OWNERS'))
                .map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </Select>
          )}
        </Field>

        <p className="rounded-lg border border-line bg-surface-2 px-3.5 py-2.5 text-xs leading-relaxed text-ink-soft">
          {roles.find((r) => r.key === form.role)?.blurb}
        </p>
      </form>
    </Modal>
  );
}

export function TeamPage() {
  const { user } = useAuth();
  const toast = useToast();
  const { data, error, loading, refetch } = useResource('/dashboard/team');
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState(null);

  const changeRole = useMutation(async (member, role) => {
    await http.patch(`/dashboard/team/${member.id}`, { role });
    toast.success(`${member.name || member.email} is now ${role}`);
    refetch();
  });

  const remove = useMutation(async () => {
    await http.delete(`/dashboard/team/${removing.id}`);
    toast.success(`${removing.name || removing.email} removed`);
    setRemoving(null);
    refetch();
  });

  const roles = data?.roles ?? [];
  const canManageOwners = can(user?.role, 'MANAGE_OWNERS');

  return (
    <>
      <PageHero
        icon={Users}
        chip="Account"
        title="Team"
        blurb="Who can sign in, and what each of them may do. A cashier takes money at the till; only an owner or an admin ever sees a key."
        actions={<Button icon={UserPlus} onClick={() => setInviting(true)}>Invite</Button>}
      />

      {loading && <Loading label="Loading your team…" />}
      {error && <ErrorNotice error={error} onRetry={refetch} title="Could not load your team" />}

      {data && (
        <>
          <Card>
            <CardHeader title="People" blurb={`${data.members.length} with access`} />

            {data.members.length === 0 ? (
              <EmptyState
                title="Just you"
                blurb="Invite the people who work here so each of them signs in as themselves."
                action={<Button icon={UserPlus} onClick={() => setInviting(true)}>Invite somebody</Button>}
              />
            ) : (
              <Table>
                <THead>
                  <TR><TH>Person</TH><TH>Role</TH><TH>Status</TH><TH align="right">Last seen</TH><TH /></TR>
                </THead>
                <TBody>
                  {data.members.map((member) => {
                    const isMe = member.id === user?.id;
                    // Rank, not equality: an admin may not touch an owner.
                    const mayEdit = !isMe
                      && (canManageOwners || member.role !== 'owner');

                    return (
                      <TR key={member.id}>
                        <TD>
                          <div className="font-medium text-ink">
                            {member.name || member.email}
                            {isMe && <span className="ml-2 text-xs font-normal text-muted">you</span>}
                          </div>
                          <div className="text-xs text-muted">{member.email}</div>
                        </TD>

                        <TD>
                          {mayEdit ? (
                            <Select
                              value={member.role}
                              className="h-8 text-xs"
                              disabled={changeRole.loading}
                              onChange={(e) => changeRole.mutate(member, e.target.value).catch(() => {})}
                            >
                              {roles
                                .filter((r) => r.key !== 'owner' || canManageOwners)
                                .map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                            </Select>
                          ) : (
                            <span className="text-sm capitalize text-ink-soft">{member.role}</span>
                          )}
                        </TD>

                        <TD>
                          {member.status === 'invited' ? (
                            <span className="chip-warning">
                              <Clock className="h-3 w-3" aria-hidden="true" /> Invited
                            </span>
                          ) : (
                            <StatusBadge status={member.status} size="sm" />
                          )}
                        </TD>

                        <TD align="right" className="text-xs text-muted">
                          {member.lastLoginAt ? formatTimestamp(member.lastLoginAt) : '—'}
                        </TD>

                        <TD align="right">
                          {mayEdit && (
                            <button
                              type="button"
                              onClick={() => setRemoving(member)}
                              className="rounded-md p-1.5 text-faint transition-colors hover:bg-red-50 hover:text-red-600"
                              aria-label={`Remove ${member.name || member.email}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </Card>

          <Card className="mt-6">
            <CardHeader title="What each role can do" />
            <CardBody className="grid gap-4 sm:grid-cols-2">
              {roles.map((role) => (
                <div key={role.key} className="rounded-xl border border-line bg-surface-2 p-4">
                  <p className="text-sm font-semibold text-ink">{role.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted">{role.blurb}</p>
                </div>
              ))}
            </CardBody>
          </Card>
        </>
      )}

      <InviteDialog
        open={inviting}
        onClose={() => setInviting(false)}
        roles={roles}
        myRole={user?.role}
        onInvited={refetch}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove.mutate().catch(() => {})}
        loading={remove.loading}
        title={`Remove ${removing?.name || removing?.email}?`}
        consequence={
          'They lose access immediately and any session they have open stops working. '
          + 'Payments they took are unaffected — those are recorded against an id, not a login. '
          + 'You can invite them again later.'
        }
        confirmLabel="Remove"
      />
    </>
  );
}

export default TeamPage;
